// Pure helpers that build click-to-chat / click-to-email deep links from
// team-member contact fields. These are used by the admin TeamsManager so that
// team coordination can happen entirely client-side without any backend
// broadcast infrastructure.

/** Very light email plausibility check — mailto: is forgiving but we don't
 * want obvious junk. The single definition shared by all link builders and
 * `VasTeamImpl.memberEmails()`. */
export function isPlausibleEmail(raw: string | undefined): boolean {
  const email = (raw || '').trim();
  return email.includes('@') && email.length >= 5;
}

/** Strip to digits only — wa.me needs the number in international form with
 * no '+', spaces, hyphens or parens. */
export function normalisePhoneForWhatsApp(raw: string): string | null {
  const digits = (raw || '').replace(/\D/g, '');
  return digits.length >= 6 ? digits : null; // shorter can't be a real MSISDN
}

export function whatsappLinkFor(phone: string | undefined, text?: string): string | null {
  const msisdn = phone ? normalisePhoneForWhatsApp(phone) : null;
  if (!msisdn) return null;
  const base = `https://wa.me/${msisdn}`;
  if (!text) return base;
  return `${base}?text=${encodeURIComponent(text)}`;
}

/** Build a mailto: link for one or many recipients. Uses `bcc` when there is
 * more than one address so the group's contact list isn't leaked to every
 * recipient. */
export function mailtoLinkFor(
  emails: string[],
  opts?: { subject?: string; body?: string }
): string | null {
  const clean = (emails || [])
    .map(e => (e || '').trim())
    .filter(isPlausibleEmail);
  if (clean.length === 0) return null;

  const params: string[] = [];
  if (opts?.subject) params.push(`subject=${encodeURIComponent(opts.subject)}`);
  if (opts?.body) params.push(`body=${encodeURIComponent(opts.body)}`);

  if (clean.length === 1) {
    const qs = params.length ? `?${params.join('&')}` : '';
    return `mailto:${clean[0]}${qs}`;
  }

  // Multi-recipient: hide addresses via bcc.
  params.unshift(`bcc=${encodeURIComponent(clean.join(','))}`);
  return `mailto:?${params.join('&')}`;
}

/** True when the string looks like a plausible WhatsApp invite URL. Used to
 * decide whether the per-team "Open group" button should render. */
export function isWhatsAppGroupLink(url: string | undefined): boolean {
  if (!url) return false;
  try {
    const u = new URL(url.trim());
    return /(^|\.)whatsapp\.com$/.test(u.hostname) && u.pathname.startsWith('/');
  } catch {
    return false;
  }
}
